/**
 * Domains resource hooks. Mirrors the API endpoint shape from
 * `api/src/domains/domains.routes.ts`:
 *
 *   - GET    /api/admin/sites/:siteId/domains — list (no pagination at MVP)
 *   - POST   /api/admin/sites/:siteId/domains — create
 *   - PATCH  /api/admin/domains/:id           — update (e.g. set primary)
 *   - DELETE /api/admin/domains/:id           — soft delete
 *
 * The list endpoint returns a paginated envelope with `meta`, but at MVP
 * a site rarely has more than a handful of domains so we don't expose
 * pagination controls in the SPA — we just request a generous page size
 * once and render everything.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateDomainInput,
  DomainResponse,
  ServerInfoResponse,
  UpdateDomainInput,
  VerifyDomainResponse,
} from "@ottopilot/shared";
import { apiDelete, apiGet, apiList, apiPatch, apiPost } from "../lib/api";
import type { ApiClientError } from "../lib/api";
import { queryKeys } from "./queryKeys";

const DEFAULT_DOMAIN_PAGE_SIZE = 100;

export function useDomains(siteId: string | undefined) {
  return useQuery<DomainResponse[], ApiClientError>({
    queryKey: queryKeys.domains.listForSite(siteId ?? ""),
    queryFn: async () => {
      const result = await apiList<DomainResponse[]>(`/api/admin/sites/${siteId ?? ""}/domains`, {
        query: { page: 1, pageSize: DEFAULT_DOMAIN_PAGE_SIZE },
      });
      return result.data;
    },
    enabled: Boolean(siteId),
  });
}

export function useCreateDomain(siteId: string) {
  const qc = useQueryClient();
  return useMutation<DomainResponse, ApiClientError, CreateDomainInput>({
    mutationFn: (input) => apiPost<DomainResponse>(`/api/admin/sites/${siteId}/domains`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.domains.all });
    },
  });
}

export function useUpdateDomain() {
  const qc = useQueryClient();
  return useMutation<DomainResponse, ApiClientError, { id: string; input: UpdateDomainInput }>({
    mutationFn: ({ id, input }) => apiPatch<DomainResponse>(`/api/admin/domains/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.domains.all });
    },
  });
}

export function useDeleteDomain() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, { id: string }>({
    mutationFn: ({ id }) => apiDelete<void>(`/api/admin/domains/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.domains.all });
    },
  });
}

/**
 * Verify a custom domain's DNS configuration (custom-domain P3, issue #293).
 *
 * Calls the API verify endpoint, which does a DNS A lookup against the
 * stored hostname and compares to `VPS_PUBLIC_IP`. On a match the server
 * flips the row to ACTIVE; on a miss the row stays PENDING_VERIFICATION
 * and the response carries the diff (expected vs observed) so the SPA can
 * render a useful "DNS not pointing here yet — found X, expected Y"
 * helper without a second round-trip.
 *
 * The response is always HTTP 200 — discriminate on `result.ok`. Only true
 * errors (auth, DB, network) reach the mutation's `onError`.
 */
export function useVerifyDomain() {
  const qc = useQueryClient();
  return useMutation<VerifyDomainResponse, ApiClientError, { id: string }>({
    mutationFn: ({ id }) => apiPost<VerifyDomainResponse>(`/api/admin/domains/${id}/verify`),
    onSuccess: (result) => {
      // Only invalidate on success — a DNS-miss verdict leaves the row
      // unchanged, so the cached list is still accurate.
      if (result.ok) {
        void qc.invalidateQueries({ queryKey: queryKeys.domains.all });
      }
    },
  });
}

/**
 * Server-info fetch (custom-domain P3, issue #293). Currently returns only
 * `vpsPublicIp` — the value the DNS-instructions panel needs to render
 * "Point your A record to <ip>". Cached indefinitely within the session;
 * the IP is environment-pinned and won't change without a redeploy.
 */
export function useServerInfo() {
  return useQuery<ServerInfoResponse, ApiClientError>({
    queryKey: ["serverInfo"],
    queryFn: () => apiGet<ServerInfoResponse>("/api/admin/server-info"),
    staleTime: Infinity,
  });
}
